import { Button, Grid } from '@mui/material';
import React from 'react';
import { useEffect, useRef, useState } from 'react';
import { desktopHeaderStyles } from './styles';
import { NavLink } from 'react-router-dom';
import { MenuWithDropdown } from './MenuWithDropdown';
import { DesktopHeaderMoreMenu } from './DesktopHeaderMoreMenu';
import { MenuWithChildren } from '../../../types/menu';

export const DesktopHeaderIntersectionObserver: React.FC<{
  menus: MenuWithChildren[];
}> = ({ menus }) => {
  const navRef = useRef<HTMLElement>(null);
  const moreMenuTogglerRef = useRef<HTMLDivElement>(null);
  const [menuVisibilityMap, setMenuVisibilityMap] = useState<Record<string, boolean>>({});

  /**
   * Handle the show and hide status of the menu
   * @param entries IntersectionObserverEntry[]
   */
  const handleIntersection = (entries: IntersectionObserverEntry[]) => {
    const updatedMenuVisibilityMap: Record<string, boolean> = {};
    entries.forEach((entry) => {
      // Note that we are using "data-menuid" to identify menu links
      const menuId = entry.target.getAttribute('data-menuid');
      if (menuId) {
        updatedMenuVisibilityMap[menuId] = entry.isIntersecting;
      }
    });
    setMenuVisibilityMap((prevMap) => ({
      ...prevMap,
      ...updatedMenuVisibilityMap,
    }));
  };

  useEffect(() => {
    const moreMenuToggler = moreMenuTogglerRef.current;
    const moreMenuWidth = moreMenuToggler?.clientWidth || 48;
    const observer = new IntersectionObserver(handleIntersection, {
      root: navRef.current,
      threshold: 0,
      rootMargin: `0px -${moreMenuWidth}px 0px 0px`, // Here -32px is a margin to trigger handleIntersection
    });

    /**
     * Adding observers to child elments of the nav with navRef
     * Note that we are using "data-menuid" attribute only to add observers
     */
    if (navRef.current) {
      Array.from(navRef.current.children).forEach((child) => {
        if (child.hasAttribute('data-menuid')) {
          observer.observe(child);
        }
      });
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  const isMenuVisible = (menuId: number | string): boolean => {
    return menuVisibilityMap[menuId.toString()];
  };

  const hiddenMenus = menus.filter((menu) => !menuVisibilityMap[menu.id.toString()]);

  return (
    <Grid
      component="nav"
      ref={navRef}
      container
      alignItems="center"
      columnSpacing={2}
      sx={desktopHeaderStyles.navLinkContainer}
    >
      {menus.map((menu, index) => (
        <Grid
          sx={{
            visibility: isMenuVisible(menu.id) ? 'visible' : 'hidden',
            order: isMenuVisible(menu.id) ? index : index + 1,
          }}
          data-menuid={menu.id}
          item
          key={menu.id}
        >
          {menu.children && menu.children.length > 0 ? (
            <MenuWithDropdown menu={menu} />
          ) : (
            <Button to={menu.link} sx={desktopHeaderStyles.menuLink} color="secondary" component={NavLink} end>
              {menu.display_name}
            </Button>
          )}
        </Grid>
      ))}
      <Grid
        ref={moreMenuTogglerRef}
        item
        sx={{
          visibility: hiddenMenus.length > 0 && hiddenMenus.length !== menus.length ? 'visible' : 'hidden',
          order: menus.length - hiddenMenus.length,
        }}
      >
        <DesktopHeaderMoreMenu menus={hiddenMenus} />
      </Grid>
    </Grid>
  );
};
